import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRoutines } from '@battleops/client/src/context/ProcessContext';
import { confirm, alert } from './Dialog';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useProjects } from '@battleops/client/src/context/ProjectContext';
import { useDepartments } from '@battleops/client/src/context/DepartmentContext';
import { currentStep, runProgress, normalizeRoutine, computeCardActiveTime, formatRoutineMinutes, formatClockMin, msToMin, routineStats, localToday, addDaysStr, matchesEvent } from '@battleops/shared';
import Icon from './Icon';
import { useNow, useKey } from '../hooks';
import { useRoutineModeOpen, openRoutineMode, closeRoutineMode } from '../lib/routineMode';
import s from './RoutineMode.module.css';

// Routine mode on the desk (docs/plans/routines.md): the window becomes the routine — its colour and icon, its steps
// in its layout (cards / focus / checklist / guided). Enter = done, S = skip, Esc = pause and leave. When it's closed
// with a run going, a pill in the corner brings it back. The last step shows how it went.
const mmss = (ms) => { const t = Math.max(0, Math.round(ms / 1000)); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`; };

/** One capture input (routine steps; the task-done metric sheet reuses it). */
export function Capture({ step, def, value, onChange, accent }) {
  const type = def?.capture?.type || step.capture;
  if (!type) return null;
  const label = def?.capture?.label && def.capture.label !== step.title ? def.capture.label : null;
  const unit = def?.capture?.unit || null;
  if (type === 'number') return (
    <div className={s.cap}>{label ? <span className="label">{label}</span> : null}
      <div className="row"><input className={s.capNum} style={{ borderColor: accent }} type="number" inputMode="decimal" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} placeholder="0" autoFocus />{unit ? <span className={s.unit}>{unit}</span> : null}</div>
    </div>
  );
  if (type === 'rating') return <div className={s.cap}><div className="row">{[1, 2, 3, 4, 5].map((n) => <button key={n} className={s.rate} data-on={value === n || undefined} style={value === n ? { background: accent, borderColor: accent } : null} onClick={() => onChange(n)}>{n}</button>)}</div></div>;
  if (type === 'yesno') return <div className={s.cap}><div className="row">{[[true, 'Yes'], [false, 'No']].map(([v, l]) => <button key={l} className={s.yn} style={value === v ? { background: accent, borderColor: accent } : null} onClick={() => onChange(v)}>{l}</button>)}</div></div>;
  if (type === 'text') return <div className={s.cap}><textarea className="input" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={label || 'A line…'} /></div>;
  if (type === 'photo') return <div className={s.cap}><span className="faint">Photos are taken on the phone.</span></div>;
  return null;
}

function Ring({ pct, accent, children }) {
  const size = 260; const r = size / 2 - 10; const c = 2 * Math.PI * r;
  return (
    <div className={s.ring} style={{ width: size, height: size }}>
      <svg width={size} height={size}><circle cx={size / 2} cy={size / 2} r={r} stroke="var(--c-border)" strokeWidth="10" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={accent} strokeWidth="10" fill="none" strokeLinecap="round" strokeDasharray={`${c} ${c}`} strokeDashoffset={c * (1 - Math.min(1, Math.max(0, pct)))} transform={`rotate(-90 ${size / 2} ${size / 2})`} /></svg>
      <div className={s.ringIn}>{children}</div>
    </div>
  );
}

function Summary({ run, routine, runs, routines, onClose, onStartNext, onBack, onReset }) {
  const st = useMemo(() => (routine ? routineStats(routine, runs, { from: addDaysStr(localToday(), -59), to: localToday() }) : null), [routine, runs]);
  const next = routines.find((r) => r.id !== routine?.id && matchesEvent(r, { type: 'after', routineId: routine?.id, at: Date.now() }));
  const m = run.metrics || {};
  const goals = run.goalResults || [];
  return (
    <div className={s.summary}>
      <div className={s.sumIcon}>{routine?.look?.icon || '✅'}</div>
      <div className={s.kicker} style={{ color: routine?.look?.color }}>{run.status === 'done' ? 'ROUTINE DONE' : 'ROUTINE ENDED'}</div>
      <h1 className={s.sumTitle}>{routine?.name || run.routineName}</h1>
      <div className={s.sumStats}>
        <div><b className="num">{m.durationMin != null ? formatRoutineMinutes(m.durationMin) : '—'}</b><span>took</span></div>
        <div><b className="num">{m.finishMin != null ? formatClockMin(m.finishMin) : '—'}</b><span>finished</span></div>
        <div><b className="num" style={{ color: 'var(--c-gold)' }}>{st?.streak?.current ? `🔥 ${st.streak.current}` : '—'}</b><span>streak</span></div>
      </div>
      {goals.length ? (
        <div className={s.goals}>
          {goals.map((g) => <div key={g.id} className="row"><Icon name={g.met === true ? 'check-circle' : g.met === false ? 'x-circle' : 'minus'} size={16} style={{ color: g.met === true ? 'var(--c-success)' : g.met === false ? 'var(--c-danger)' : 'var(--c-text-faint)' }} /><span>{g.label}</span></div>)}
        </div>
      ) : null}
      {st?.score != null ? <div className="faint">Score {st.score}{st.hitRate != null ? ` · ${Math.round(st.hitRate)}% of days` : ''}</div> : null}
      <div className="row" style={{ marginTop: 24, justifyContent: 'center' }}>
        {next ? <button className="btn primary" style={{ background: next.look.color, borderColor: next.look.color }} onClick={() => onStartNext(next)}>Start {next.name}</button> : null}
        <button className="btn" onClick={onClose} autoFocus>Close</button>
      </div>
      <div className="row" style={{ marginTop: 10, justifyContent: 'center' }}>
        <button className="btn ghost" onClick={onBack} title="Open the run again on its last step"><Icon name="corner-up-left" size={15} />Back a step</button>
        <button className="btn ghost" style={{ color: 'var(--c-danger)' }} onClick={onReset} title="Delete this run (untouched steps go; logged time stays)"><Icon name="trash-2" size={15} />Throw away</button>
      </div>
    </div>
  );
}

export default function RoutineMode() {
  const open = useRoutineModeOpen();
  const { activeRun, runs, rawRoutines, routines, stepDone, skipStep, backStep, resetRun, pauseRoutine, resumeRoutine, finishRoutine, captureValue, startRoutine, setRunProject } = useRoutines();
  const { timer } = useTasks();
  const { projects } = useProjects();
  const { deptForProject } = useDepartments();
  const now = useNow(1000);
  const [endedId, setEndedId] = useState(null);
  const [draft, setDraft] = useState({});
  const run = activeRun || (endedId ? runs.find((x) => x.id === endedId) : null);
  const routine = useMemo(() => { const raw = run ? rawRoutines.find((x) => x.id === run.routineId) : null; return raw ? normalizeRoutine(raw) : null; }, [run?.routineId, rawRoutines]);
  const accent = routine?.look?.color || 'var(--c-accent)';
  const layout = routine?.look?.layout || 'cards';
  const step = run?.status === 'running' ? currentStep(run) : null;
  const def = step ? routine?.steps.find((x) => x.stepId === step.stepId) : null;
  const idx = step ? run.steps.findIndex((x) => x.stepId === step.stepId) : -1;
  const spoken = useRef(null);
  const showing = open && !!run;
  // A routine that asks which project: the run's project, changeable right in the mode.
  const asks = routine?.projectMode === 'ask';
  const pickList = useMemo(() => {
    if (!asks) return [];
    const open = projects.filter((p) => !p.archived && !['archived', 'completed', 'cancelled'].includes(p.status));
    const home = routine?.projectId ? deptForProject(routine.projectId).id : routine?.departmentId || null;
    return open.slice().sort((a, b) => (deptForProject(a.id).id === home ? 0 : 1) - (deptForProject(b.id).id === home ? 0 : 1) || String(a.name).localeCompare(String(b.name)));
  }, [asks, projects, routine?.projectId, routine?.departmentId]);
  // A step action that fails says so (it used to only warn in the console, so a button looked dead).
  const fail = (e) => { console.warn(e); alert({ title: 'That didn’t work', message: String(e?.message || e) }); };
  const pickProject = (pid) => { if (!run || (run.projectId || null) === (pid || null)) return; try { setRunProject(run.id, pid || null); } catch (e) { fail(e); } };

  useEffect(() => { if (open && activeRun?.pausedAt) { try { resumeRoutine(activeRun.id); } catch (e) { console.warn(e); } } }, [open, activeRun?.id]);

  const stepMs = step ? (timer?.taskId === step.taskId ? computeCardActiveTime(timer, now) : (step.startedAt ? now - step.startedAt : 0)) : 0;
  const plannedMs = (def?.minutes || step?.minutes || 0) * 60000;
  const valueFor = (st) => (draft[st.stepId] !== undefined ? draft[st.stepId] : st.value);

  const done = (st = step) => {
    if (!st || !run) return;
    const v = valueFor(st);
    try { const r = stepDone(run.id, { stepId: st.stepId, ...(v !== undefined && v !== null && v !== '' ? { value: v } : {}) }); if (r?.finished) setEndedId(run.id); } catch (e) { fail(e); }
  };
  const skip = (st = step) => { if (!st || !run) return; try { const r = skipStep(run.id, { stepId: st.stepId }); if (r?.finished) setEndedId(run.id); } catch (e) { fail(e); } };
  const leave = () => { if (activeRun && !activeRun.pausedAt) { try { pauseRoutine(activeRun.id); } catch (e) { console.warn(e); } } closeRoutineMode(); };
  const endNow = () => { if (!run) return; try { finishRoutine(run.id); setEndedId(run.id); } catch (e) { fail(e); } };
  const back = () => { if (!run) return; try { backStep(run.id); setEndedId(null); } catch (e) { fail(e); } };
  const reset = () => { if (!run) return; confirm({ title: 'Throw this run away?', message: 'The run is deleted and its untouched steps go. Time already logged on a step stays on its task.', icon: 'trash-2', confirmLabel: 'Throw away' }).then((ok) => { if (!ok) return; try { resetRun(run.id); setEndedId(null); closeRoutineMode(); } catch (e) { fail(e); } }); };
  const setValue = (st, v) => { setDraft((d) => ({ ...d, [st.stepId]: v })); if (['rating', 'yesno'].includes(st.capture)) captureValue(run.id, st.stepId, v); };

  useKey('enter', () => { if (showing && step && layout !== 'checklist') done(step); }, { enabled: showing, inFields: true });
  useKey('s', () => { if (showing && step) skip(step); }, { enabled: showing });
  useKey('b', () => { if (showing && step && idx > 0) back(); }, { enabled: showing });
  useKey('escape', () => { if (showing) { if (run?.status === 'running') leave(); else { setEndedId(null); closeRoutineMode(); } } }, { enabled: showing, inFields: true });

  const auto = layout === 'guided' || (layout === 'focus' && routine?.look?.autoAdvance);
  useEffect(() => { if (showing && auto && step && plannedMs && stepMs >= plannedMs && !def?.capture) done(step); }, [showing, auto, step?.stepId, stepMs >= plannedMs]);
  useEffect(() => {
    if (!showing || layout !== 'guided' || !step || spoken.current === step.stepId || typeof window === 'undefined' || !window.speechSynthesis) return;
    spoken.current = step.stepId;
    try { window.speechSynthesis.speak(new SpeechSynthesisUtterance(`${step.title}${def?.minutes ? `. ${def.minutes} minutes.` : ''}`)); } catch { /* no speech */ }
  }, [showing, layout, step?.stepId]);

  // closed, run going → the pill
  if (!showing) {
    if (!activeRun) return null;
    const p = runProgress(activeRun);
    const r = rawRoutines.find((x) => x.id === activeRun.routineId);
    const col = r?.look?.color || r?.color || 'var(--c-accent)';
    return (
      <button className={s.pill} style={{ borderColor: col }} onClick={openRoutineMode} title="Back into the routine">
        <span>{r?.look?.icon || '🔁'}</span>
        <span><b>{activeRun.routineName}</b> {activeRun.pausedAt ? 'paused' : 'going'} · step {Math.min(p.done + p.skipped + 1, p.total)} of {p.total}</span>
        <span className={s.pillGo} style={{ color: col }}>{activeRun.pausedAt ? 'Back in' : 'Open'}</span>
      </button>
    );
  }

  const body = (st) => {
    const d = routine?.steps.find((x) => x.stepId === st.stepId);
    return (
      <>
        <div className={s.stepTitle}>{st.title}</div>
        {d?.note ? <div className={s.note}>{d.note}</div> : null}
        <Capture step={st} def={d} value={valueFor(st)} onChange={(v) => setValue(st, v)} accent={accent} />
      </>
    );
  };

  return (
    <div className={s.overlay} role="dialog" aria-modal="true" aria-label={`${routine?.name || 'Routine'} — routine mode`} style={{ '--rc': accent }}>
      <div className={s.glow} />
      {run.status !== 'running' ? (
        <Summary run={run} routine={routine} runs={runs} routines={routines}
          onClose={() => { setEndedId(null); closeRoutineMode(); }}
          onStartNext={(r) => { try { startRoutine(r.id, { trigger: { type: 'after', at: Date.now() } }); setEndedId(null); } catch (e) { fail(e); } }}
          onBack={back} onReset={reset} />
      ) : (
        <div className={s.frame}>
          <header className={s.strip}>
            <button className="btn ghost" onClick={leave} title="Pause and leave (Esc)"><Icon name="chevron-down" size={18} />Pause</button>
            <div className={s.stripMid}>
              <div className={s.kicker} style={{ color: accent }}>{routine?.look?.icon} {routine?.look?.kicker || run.routineName}</div>
              <div className="faint num">Step {Math.min(idx + 1, run.steps.length)} of {run.steps.length} · {mmss(now - (run.startedAt || now))}</div>
            </div>
            <button className="btn ghost" onClick={endNow} title="End the routine now"><Icon name="flag" size={16} />End</button>
          </header>
          {asks ? (
            <label className={s.forRow} data-unpicked={run.projectId ? undefined : ''}>
              <span>For</span>
              <select className="input" value={run.projectId || ''} onChange={(e) => pickProject(e.target.value)} aria-label="Which project this run is for">
                {!run.projectId ? <option value="">Pick the project…</option> : null}
                {pickList.map((p) => <option key={p.id} value={p.id}>{p.icon ? `${p.icon} ` : ''}{p.name} · {deptForProject(p.id).name}</option>)}
              </select>
            </label>
          ) : null}
          <div className={s.bar}>{run.steps.map((x) => <span key={x.stepId} style={{ background: x.doneAt ? accent : x.skipped ? 'var(--c-border-strong)' : x.stepId === step?.stepId ? `color-mix(in srgb, ${accent} 45%, transparent)` : 'var(--c-border)' }} />)}</div>

          {layout === 'checklist' ? (
            <div className={s.list}>
              {run.steps.map((x) => {
                const isDone = !!x.doneAt; const isSkip = !!x.skipped;
                return (
                  <div key={x.stepId} className={s.checkRow} data-current={x.stepId === step?.stepId || undefined}>
                    <button className={s.box} data-on={isDone || undefined} style={isDone ? { background: accent, borderColor: accent } : null} disabled={isDone || isSkip} onClick={() => done(x)} aria-label={`Done: ${x.title}`}>{isDone ? <Icon name="check" size={16} /> : null}</button>
                    <div style={{ flex: 1 }}>
                      <div className={s.checkTitle} data-struck={isDone || isSkip || undefined}>{x.title}</div>
                      {!isDone && !isSkip ? <Capture step={x} def={routine?.steps.find((d) => d.stepId === x.stepId)} value={valueFor(x)} onChange={(v) => setValue(x, v)} accent={accent} /> : null}
                    </div>
                    {x.minutes ? <span className="faint">{x.minutes} min</span> : null}
                    {!isDone && !isSkip ? <button className="btn ghost sm" onClick={() => skip(x)}>Skip</button> : null}
                  </div>
                );
              })}
              <button className="btn primary" style={{ background: accent, borderColor: accent, marginTop: 16, height: 48 }} onClick={endNow}>Finish</button>
            </div>
          ) : step ? (
            <div className={s.stage}>
              {layout === 'cards' ? (
                <div className={s.card} key={step.stepId}>
                  <div className={s.stepNum} style={{ color: accent }}>STEP {idx + 1}</div>
                  {body(step)}
                  <div className={s.time}><span className="num" style={{ color: plannedMs && stepMs > plannedMs ? 'var(--c-warning)' : undefined }}>{mmss(stepMs)}</span>{plannedMs ? <span className="faint num"> / {mmss(plannedMs)}</span> : null}</div>
                </div>
              ) : (
                <>
                  <Ring pct={plannedMs ? stepMs / plannedMs : 0} accent={accent}>
                    <div className={s.ringTime}>{plannedMs ? mmss(Math.max(0, plannedMs - stepMs)) : mmss(stepMs)}</div>
                    <div className="faint">{plannedMs ? (stepMs > plannedMs ? `over by ${mmss(stepMs - plannedMs)}` : 'left') : 'so far'}</div>
                  </Ring>
                  <div className={s.focusBody}>{body(step)}</div>
                </>
              )}
              {run.steps.slice(idx + 1).find((x) => !x.doneAt && !x.skipped) ? <div className="faint" style={{ marginTop: 18 }}>Next: {run.steps.slice(idx + 1).find((x) => !x.doneAt && !x.skipped).title}</div> : null}
              <div className={s.actions}>
                {idx > 0 ? <button className="btn ghost" onClick={back} title="Back to the previous step (B)"><Icon name="corner-up-left" size={16} />Back</button> : null}
                <button className="btn" onClick={() => skip(step)} title="Skip (S)"><Icon name="skip-forward" size={16} />Skip</button>
                <button className="btn primary" style={{ background: accent, borderColor: accent }} onClick={() => done(step)} title="Done (Enter)"><Icon name="check" size={18} />{run.steps.slice(idx + 1).some((x) => !x.doneAt && !x.skipped) ? 'Done' : 'Finish'}</button>
              </div>
              <div className={s.keys}><span className="kbd">Enter</span> done · <span className="kbd">S</span> skip · <span className="kbd">B</span> back · <span className="kbd">Esc</span> pause</div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
